import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Artifacts } from '@/api/EmissionReportModel'
import { Card, Table, TableColumnHeader, TableRow } from '@/common'
import { EmissionTypeToReport } from './ProcessEmissionsSection'
import { ReportSectionCardHeader } from '../SectionCardHeader'
import { EmissionsIcon } from '@/common/components/icons'
import { ProductionRouteAsset } from '@/features/production-routes/api/ProductionRouteModel'
import Co2ProcessEmissionsTableRow from './Co2ProcessEmissionTableRow'
import { EmissionCalculationMethods } from '@/shared/hooks/api/appConfig'
import CementOutputBasedCalculatorPanel from '../calculators/cement/CementOutputBasedCalculatorPanel'
import CementClinkerInputBasedCalculatorDialog from '../calculators/cement/CementClinkerInputBasedCalculatorDialog'
import FlueGasCleaningCalculatorPanel from '../calculators/flueGasCleaning/FlueGasCleaningCalculatorPanel'
import AluminiumPrebakeAnodeCalculatorPanel from '../calculators/aluminium/AluminiumPrebakeAnodeCalculatorPanel'
import AluminiumSoderbergAnodeCalculatorPanel from '../calculators/aluminium/AluminiumSoderbergCalculatorPanel'

type EmissionEditState = {
  productionRouteAssetId: number
  emissionRecordId?: string
  method: string
}

export default function Co2ProcessEmissionReporting({
  isViewMode,
  emissionsToReport,
  onEmissionRemove,
  onEmissionSubmit,
  activityLevel
}: {
  isComplete: boolean
  emissionsToReport: EmissionTypeToReport[]
  isViewMode: boolean
  onEmissionRemove: (emissionId: string) => void
  onEmissionSubmit: (
    payload: {
      emissionType: string
      method: string
      emission: number
      artifacts: Artifacts
      asset: ProductionRouteAsset
    },
    id?: string
  ) => void
  activityLevel: number
}) {
  const { t } = useTranslation()
  const totalReportedEmissions = useMemo(() => {
    return emissionsToReport.reduce(
      (acc, emission) => acc + (emission.emissionRecord?.emission || 0),
      0
    )
  }, [emissionsToReport])

  const [editState, setEditState] = useState<EmissionEditState | null>(null)

  const editedProductionRouteAsset = useMemo(() => {
    if (editState === null) {
      return null
    }

    return (
      emissionsToReport.find(
        emission => emission.productionRouteAsset.id === editState.productionRouteAssetId
      ) || null
    )
  }, [editState, emissionsToReport])

  const isAddingCementClinkerInputBased = Boolean(
    editState?.method === EmissionCalculationMethods.CementClinkerInputBased
  )

  const isAddingCementClinkerOutputBased = Boolean(
    editState?.method === EmissionCalculationMethods.CementClinkerOutputBased
  )

  const isAddingFlueGasCleaning = editState?.method === EmissionCalculationMethods.FlueGasCleaning
  const isAddingAluminiumPrebakeAnode =
    editState?.method === EmissionCalculationMethods.AluminiumPrebakeAnode
  const isAddingAluminiumSoderbergAnode =
    editState?.method === EmissionCalculationMethods.AluminiumPrebakeSoderberg

  const onSubmit = useCallback(
    (emission: number, artifacts: Artifacts) => {
      if (editedProductionRouteAsset === null || !editState?.method) {
        return
      }

      const existingRecord = editedProductionRouteAsset.emissionRecord

      onEmissionSubmit(
        {
          asset: editedProductionRouteAsset.productionRouteAsset,
          emissionType: 'CO2',
          method: editState.method,
          emission,
          artifacts
        },
        existingRecord?.id
      )
    },
    [editedProductionRouteAsset]
  )

  const onRemove = useCallback(
    (id: string) => {
      onEmissionRemove(id)
      setEditState(null)
    },
    [onEmissionRemove]
  )

  const percentageOfAssetsWithEmissions = useMemo(() => {
    return (
      (emissionsToReport.filter(emission => emission.emissionRecord?.emission! >= 0).length /
        emissionsToReport.length) *
      100
    )
  }, [emissionsToReport])

  return (
    <>
      <Card>
        <ReportSectionCardHeader
          icon={<EmissionsIcon className="size-4" />}
          title={t('emissionTypes.CO2')}
          emissions={totalReportedEmissions}
          completionPercentage={percentageOfAssetsWithEmissions}
        />
        <Table dense borderless>
          <thead>
            <TableRow>
              <TableColumnHeader className="w-64" />
              <TableColumnHeader className="w-36">
                {t('report:processEmissions.table.headers.customId')}
              </TableColumnHeader>
              <TableColumnHeader className="w-72">
                {t('report:processEmissions.table.headers.calculationMethod')}
              </TableColumnHeader>
              <TableColumnHeader className="w-24 text-right">
                {t('report:processEmissions.table.headers.emission')}
              </TableColumnHeader>
              <TableColumnHeader className="w-44" />
            </TableRow>
          </thead>
          {emissionsToReport.map(emission => (
            <Co2ProcessEmissionsTableRow
              key={emission.productionRouteAsset.id}
              assetWithMetadata={emission}
              setIsAddingEmission={setEditState}
              isViewMode={isViewMode}
              removeEmissionRecord={onRemove}
            />
          ))}
        </Table>
      </Card>
      <CementClinkerInputBasedCalculatorDialog
        onSubmit={onSubmit}
        removeEmissionRecord={onRemove}
        emissionRecordId={editedProductionRouteAsset?.emissionRecord?.id}
        isOpen={isAddingCementClinkerInputBased}
        close={() => setEditState(null)}
        initialValues={editedProductionRouteAsset?.emissionRecord?.artifacts}
      />
      {isAddingCementClinkerOutputBased && (
        <CementOutputBasedCalculatorPanel
          onSubmit={onSubmit}
          isOpen={isAddingCementClinkerOutputBased}
          close={() => setEditState(null)}
          initialValues={editedProductionRouteAsset?.emissionRecord?.artifacts}
        />
      )}
      {isAddingFlueGasCleaning && (
        <FlueGasCleaningCalculatorPanel
          onSubmit={onSubmit}
          isOpen={isAddingFlueGasCleaning}
          close={() => setEditState(null)}
          initialValues={editedProductionRouteAsset?.emissionRecord?.artifacts}
        />
      )}
      <AluminiumPrebakeAnodeCalculatorPanel
        addEmission={onSubmit}
        isOpen={isAddingAluminiumPrebakeAnode}
        close={() => setEditState(null)}
        initialValues={editedProductionRouteAsset?.emissionRecord?.artifacts}
      />
      <AluminiumSoderbergAnodeCalculatorPanel
        addEmission={onSubmit}
        isOpen={isAddingAluminiumSoderbergAnode}
        aluminiumQuantity={activityLevel}
        assetDefinitionId={editedProductionRouteAsset?.assetDefinition?.id}
        initialValues={editedProductionRouteAsset?.emissionRecord?.artifacts}
        close={() => setEditState(null)}
      />
    </>
  )
}
